import classNames from 'clsx';
import React, { memo, ReactElement, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useFallbackSearchUrl } from 'reactApp/hooks/useFallbackSearchUrl';
import { historyPush } from 'reactApp/modules/router/router.module';
import { resetSelect } from 'reactApp/modules/selections/selections.actions';
import { setQueryParams, sidebarToggle } from 'reactApp/modules/settings/settings.module';
import { getIdByStorage, getStorage } from 'reactApp/modules/storage/storage.helpers';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { Logo } from 'reactApp/ui/Header/Logo/Logo';
import { ToolbarItem } from 'reactApp/ui/Toolbar/ToolbarItem/ToolbarItem';
import { ChevronLeftOutlineIcon, Icon20MenuOutline } from 'reactApp/ui/VKUIIcons';
import { useWebSearchFilters } from 'reactApp/ui/WebSearch/hooks/useWebSearchFilters';

import styles from './Header.css';
import { useShowBackItem } from './Header.hooks';

interface Props {
    isSearchExpanded: boolean;
    toggleSearch: (state: boolean) => void;
    storage: EStorageType;
    isResponsiveEnable: boolean;
}

export const LeftSide = memo(({ isSearchExpanded, toggleSearch, storage, isResponsiveEnable }: Props): ReactElement => {
    const dispatch = useDispatch();

    const { isAttaches } = getStorage(storage);
    const showBackItem = useShowBackItem();
    const { closeSearch } = useFallbackSearchUrl();
    const { onReset } = useWebSearchFilters();

    const cancelSearch = useCallback(() => {
        if (isAttaches) {
            dispatch(historyPush({ id: getIdByStorage(EStorageType.attaches) }));
            dispatch(setQueryParams());
        } else {
            onReset();
            closeSearch();
        }
        toggleSearch(false);
    }, [isAttaches, toggleSearch, dispatch, onReset, closeSearch]);

    const onSidebarTogglerClick = useCallback(() => {
        dispatch(sidebarToggle());
    }, [dispatch]);

    const resetSelection = useCallback(() => {
        dispatch(resetSelect());
    }, [dispatch]);

    const content = useMemo(() => {
        if (showBackItem) {
            return (
                <ToolbarItem
                    id="back"
                    text="Вернуться"
                    collapse={isResponsiveEnable}
                    icon={<ChevronLeftOutlineIcon />}
                    onClick={resetSelection}
                />
            );
        }

        if (isSearchExpanded) {
            return (
                <ToolbarItem
                    id="closeSearch"
                    text="Сбросить поиск"
                    collapse={isResponsiveEnable}
                    icon={<ChevronLeftOutlineIcon />}
                    onClick={cancelSearch}
                />
            );
        }

        if (isResponsiveEnable) {
            const isNotPublic = storage !== EStorageType.public;
            return (
                (isNotPublic && <ToolbarItem id="sidebarToggler" icon={<Icon20MenuOutline />} onClick={onSidebarTogglerClick} />) || null
            );
        }

        return <Logo />;
    }, [cancelSearch, isSearchExpanded, showBackItem, resetSelection, isResponsiveEnable, onSidebarTogglerClick, storage]);

    return (
        <div
            className={classNames({
                [styles.leftSide]: true,
                [styles.leftSideMyTeam]: isResponsiveEnable,
            })}
        >
            {content}
        </div>
    );
});

LeftSide.displayName = 'LeftSide';
